/** 弹窗 renderer 注册与预加载映射。 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import type { IActivityPopupRendererProps } from './types';
import type { TActivityPopupType, TKnownActivityPopupType } from '@/activity-orchestration/types';

type TPopupRenderer = ComponentType<IActivityPopupRendererProps>;
type TLazyPopupRenderer = LazyExoticComponent<TPopupRenderer>;
type TPopupRendererImporter = () => Promise<{ default: TPopupRenderer }>;

const popupRendererImporters = {
    recharge_retention: () => import('./renderers/recharge-retention'),
    subscribe_retention: () => import('./renderers/subscribe-retention'),
    fake_discount: () => import('./renderers/fake-discount'),
    // 运营分发位接入正式 UI 后替换占位 renderer。
    ops_promo: () => import('./renderers/placeholder'),
} satisfies Partial<Record<TActivityPopupType, TPopupRendererImporter>>;

/**
 * type → lazy renderer。
 * 未知 / 未注册 type 不在表内 → Host 走「无 renderer」跳过路径。
 */
export const popupRenderers: Partial<Record<TActivityPopupType, TLazyPopupRenderer>> = {
    recharge_retention: lazy(popupRendererImporters.recharge_retention),
    subscribe_retention: lazy(popupRendererImporters.subscribe_retention),
    fake_discount: lazy(popupRendererImporters.fake_discount),
    ops_promo: lazy(popupRendererImporters.ops_promo),
};

function getPopupRendererImporter(type: TActivityPopupType): TPopupRendererImporter | undefined {
    return popupRendererImporters[type as keyof typeof popupRendererImporters];
}

/** 冷启 / 聚合窗口预取的已知 type 列表（§11 #12）；仅预取本期已注册正式 UI */
export const PRELOAD_TYPES: TKnownActivityPopupType[] = ['recharge_retention', 'subscribe_retention', 'fake_discount'];

/** 按 type 取 renderer；无匹配返回 undefined */
export function getPopupRenderer(type: TActivityPopupType): TLazyPopupRenderer | undefined {
    return popupRenderers[type];
}

/**
 * 预取指定 type 的 chunk（不占 current）。
 * 利用动态 import 副作用；失败静默（弱网由 Host 加载态兜底）。
 * @param type 弹窗类型
 */
export function preloadPopupRenderer(type: TActivityPopupType): void {
    const importer = getPopupRendererImporter(type);
    if (!importer) {
        return;
    }
    importer().catch(() => {});
}

/** 预取 PRELOAD_TYPES 列表 */
export function preloadKnownPopupRenderers(): void {
    PRELOAD_TYPES.forEach(preloadPopupRenderer);
}
